import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Collection from "@/pages/Collection";
import CollectionPage from "@/pages/CollectionPage";
import ProductDetail from "@/pages/ProductDetail";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Services from "@/pages/Services";
import LegalPage from "@/pages/LegalPage";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import TimberHub from "@/pages/timber/TimberHub";
import ArchitecturalPanels from "@/pages/timber/PanelsCatalogue";
import PanelDetail from "@/pages/timber/PanelDetail";
import FlooringHub from "@/pages/timber/FlooringHub";
import FlooringDetail from "@/pages/timber/FlooringDetail";
import VeneersHub from "@/pages/timber/VeneersHub";
import VeneerDetail from "@/pages/timber/VeneerDetail";
import AdditionalHub from "@/pages/additional/AdditionalHub";
import AdditionalDetail from "@/pages/additional/AdditionalDetail";
import SolidDoors from "@/pages/timber/SolidDoors";
import AmericanOak from "@/pages/timber/AmericanOak";
import CustomCarpetHub from "@/pages/CustomCarpetHub";
import Brochures from "@/pages/Brochures";
import { LEGAL } from "@/lib/site";

const ScrollManager = ({ lenisRef }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true, force: true });
    }
    window.scrollTo(0, 0);
  }, [pathname, lenisRef]);
  return null;
};

function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager lenisRef={lenisRef} />
      <Toaster position="bottom-right" theme="light" />
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/collection" element={<Collection />} />
          <Route path="/collections/timber" element={<TimberHub />} />
          <Route path="/collections/timber/american-oak" element={<AmericanOak />} />
          <Route path="/collections/timber/architectural-panels" element={<ArchitecturalPanels />} />
          <Route path="/collections/timber/architectural-panels/:profile" element={<PanelDetail />} />
          <Route path="/collections/timber/solid-doors" element={<SolidDoors />} />
          <Route path="/collections/timber/flooring" element={<FlooringHub />} />
          <Route path="/collections/timber/flooring/:sub" element={<FlooringDetail />} />
          <Route path="/collections/timber/veneers" element={<VeneersHub />} />
          <Route path="/collections/timber/veneers/:sub" element={<VeneerDetail />} />
          <Route path="/collections/additional" element={<AdditionalHub />} />
          <Route path="/collections/additional/:sub" element={<AdditionalDetail />} />
          <Route path="/collections/custom-carpet" element={<CustomCarpetHub />} />
          <Route path="/collections/:slug" element={<CollectionPage />} />
          <Route path="/collections/:slug/:product" element={<ProductDetail />} />
          <Route path="/brochures" element={<Brochures />} />
          <Route path="/services" element={<Services />} />
          <Route path="/portfolio" element={<Projects />} />
          <Route path="/portfolio/:slug" element={<ProjectDetail />} />
          <Route path="/story" element={<About />} />
          <Route path="/maintenance-warranty" element={<LegalPage data={LEGAL["maintenance-warranty"]} />} />
          <Route path="/shipping" element={<LegalPage data={LEGAL.shipping} />} />
          <Route path="/terms" element={<LegalPage data={LEGAL.terms} />} />
          <Route path="/privacy" element={<LegalPage data={LEGAL.privacy} />} />
          <Route path="/faqs" element={<Faq />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
